import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { AdminSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Briefcase, Plus, Edit, Trash2, Loader2, Save, Search } from 'lucide-react';
import { toast } from 'sonner';
import { ExportListButtons } from './ExportListButtons';

const ICON_OPTIONS = [
  'Utensils', 'ShoppingBag', 'Heart', 'Sparkles', 'Cpu', 'Plane',
  'GraduationCap', 'Music', 'Car', 'Home', 'Briefcase', 'Tag', 'Coffee',
  'Camera', 'Gift', 'Wrench', 'Scissors', 'Stethoscope', 'BookOpen', 'Building'
];

export function AdminIndustriesPage() {
  const { api } = useAuth();
  const [industries, setIndustries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', slug: '', icon: '', description: '', order: 0, is_active: true });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | inactive

  const fetchIndustries = useCallback(async () => {
    try {
      const { data } = await api.get('/api/admin/industries');
      setIndustries(data.industries || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchIndustries(); }, [fetchIndustries]);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', slug: '', icon: '', description: '', order: industries.length, is_active: true });
    setDialogOpen(true);
  };

  const openEdit = (ind) => {
    setEditing(ind);
    setForm({
      name: ind.name, slug: ind.slug, icon: ind.icon || '', description: ind.description || '',
      order: ind.order || 0, is_active: ind.is_active !== false,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSubmitting(true);
    try {
      if (editing) {
        await api.put(`/api/admin/industries/${editing.id}`, form);
        toast.success('Industry updated');
      } else {
        await api.post('/api/admin/industries', form);
        toast.success('Industry created');
      }
      setDialogOpen(false);
      fetchIndustries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
    setSubmitting(false);
  };

  const remove = async (ind) => {
    if (!window.confirm(`Deactivate industry "${ind.name}"? Merchants assigned to it will need to be reassigned.`)) return;
    try {
      await api.delete(`/api/admin/industries/${ind.id}`);
      toast.success('Industry deactivated');
      fetchIndustries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <AdminSidebarLayout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="admin-industries-page">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-outfit text-3xl font-semibold text-white flex items-center gap-3">
              <Briefcase className="w-8 h-8 text-[#00E5FF]" /> Business Industries
            </h1>
            <p className="text-[#A0B4CC] mt-1">Manage industries merchants choose during registration</p>
          </div>
          <div className="flex items-center gap-2">
            <ExportListButtons api={api} resource="industries" filename="industries" />
            <Button onClick={openCreate} className="btn-primary" data-testid="open-create-industry-btn">
              <Plus className="w-4 h-4 mr-2" /> New Industry
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF] mx-auto" /></div>
        ) : (
          <Card className="bg-[#041A3B] border-white/10">
            <CardContent className="p-0">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-4 border-b border-white/5">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C7599]" />
                  <Input
                    placeholder="Search industries..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 bg-[#000B18] border-white/10 text-white h-9 text-sm"
                    data-testid="industries-search"
                  />
                </div>
                <div className="flex gap-2">
                  {[{v:'all', l:'All'}, {v:'active', l:'Active'}, {v:'inactive', l:'Inactive'}].map(s => (
                    <button
                      key={s.v} onClick={() => setStatusFilter(s.v)}
                      data-testid={`industries-filter-${s.v}`}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        statusFilter === s.v ? 'bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/40' : 'text-[#A0B4CC] border border-white/10 hover:border-white/30'
                      }`}
                    >
                      {s.l}
                    </button>
                  ))}
                </div>
              </div>
              {(() => {
                const filtered = industries.filter(ind => {
                  if (search && !ind.name.toLowerCase().includes(search.toLowerCase()) && !(ind.slug || '').toLowerCase().includes(search.toLowerCase())) return false;
                  if (statusFilter === 'active' && !ind.is_active) return false;
                  if (statusFilter === 'inactive' && ind.is_active) return false;
                  return true;
                });
                if (filtered.length === 0) {
                  return (
                    <div className="py-16 text-center" data-testid="no-industries">
                      <Briefcase className="w-12 h-12 text-[#5C7599] mx-auto mb-3 opacity-60" />
                      <p className="text-[#A0B4CC]">
                        {search || statusFilter !== 'all' ? 'No industries match your filters' : 'No industries yet'}
                      </p>
                    </div>
                  );
                }
                return (
                  <div className="divide-y divide-white/5">
                    {filtered.map((ind) => (
                      <div key={ind.id} className="flex items-center justify-between p-4 hover:bg-white/5" data-testid={`industry-row-${ind.id}`}>
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/10 flex items-center justify-center text-[#00E5FF] font-mono text-xs">
                            {ind.order ?? '·'}
                          </div>
                          <div>
                            <p className="text-white font-medium">{ind.name}</p>
                            <p className="text-[#5C7599] text-xs">{ind.slug} {ind.icon && `· ${ind.icon}`}</p>
                          </div>
                          {!ind.is_active && <Badge className="bg-[#FF1744]/20 text-[#FF1744]">Inactive</Badge>}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEdit(ind)} className="text-[#00E5FF]" data-testid={`edit-industry-${ind.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          {ind.is_active && (
                            <Button size="sm" variant="ghost" onClick={() => remove(ind)} className="text-[#FF1744]" data-testid={`delete-industry-${ind.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#041A3B] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">{editing ? 'Edit Industry' : 'New Industry'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-white">Name *</Label>
                <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g., Food & Beverage" className="bg-[#000B18] border-white/20 text-white" data-testid="industry-name-input" />
              </div>
              <div>
                <Label className="text-white">Slug (lowercase, no spaces)</Label>
                <Input value={form.slug} onChange={e => setForm({ ...form, slug: e.target.value })} placeholder="auto-generated if blank" className="bg-[#000B18] border-white/20 text-white font-mono" />
              </div>
              <div>
                <Label className="text-white">Icon (Lucide name)</Label>
                <select value={form.icon} onChange={e => setForm({ ...form, icon: e.target.value })} className="w-full bg-[#000B18] border border-white/20 rounded-md p-2 text-white text-sm" data-testid="industry-icon-select">
                  <option value="">No icon</option>
                  {ICON_OPTIONS.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-white">Description</Label>
                <Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="bg-[#000B18] border-white/20 text-white" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-white">Display Order</Label>
                  <Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="bg-[#000B18] border-white/20 text-white" />
                </div>
                {editing && (
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-white text-sm cursor-pointer">
                      <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-[#00E5FF]" data-testid="industry-active-toggle" />
                      Active
                    </label>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-[#A0B4CC]">Cancel</Button>
              <Button onClick={save} disabled={submitting} className="btn-primary" data-testid="save-industry-btn">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" /> {editing ? 'Save' : 'Create'}</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminSidebarLayout>
  );
}
